import { useState, useEffect } from 'react'

// ── Types ─────────────────────────────────────────────────────────────────────

type Jurisdiction = 'gdpr' | 'pipeda' | 'au'

interface ConsentState {
  analytics: boolean
  timestamp: number
  version: string
}

// ── Constants ─────────────────────────────────────────────────────────────────

const CONSENT_KEY = 'em_cookie_consent'
const CONSENT_VERSION = '1.0'
const CONSENT_MAX_AGE = 365 * 24 * 60 * 60 * 1000 // 12 months in ms
const UMAMI_WEBSITE_ID = 'c51c0b6a-0a1a-46fd-b022-de78cd4f23d6'

// ── Helpers ───────────────────────────────────────────────────────────────────

function detectJurisdiction(): Jurisdiction {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone ?? ''
  // UK + EU timezones → GDPR (includes Europe/London for UK GDPR)
  if (
    tz.startsWith('Europe/') ||
    tz === 'Atlantic/Reykjavik' ||
    tz === 'Atlantic/Faroe' ||
    tz === 'Atlantic/Azores'
  ) {
    return 'gdpr'
  }
  // Canada detection via browser language (most reliable client-side signal)
  // en-CA, fr-CA, fr-QC etc.
  const langs = navigator.languages ?? [navigator.language ?? '']
  if (langs.some((l) => l.toLowerCase().endsWith('-ca'))) {
    return 'pipeda'
  }
  return 'au'
}

function loadUmami() {
  // Don't load twice
  if (document.querySelector('script[data-website-id]')) return
  const script = document.createElement('script')
  script.defer = true
  script.src = 'https://cloud.umami.is/script.js'
  script.setAttribute('data-website-id', UMAMI_WEBSITE_ID)
  document.head.appendChild(script)
}

function getSavedConsent(): ConsentState | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY)
    if (!raw) return null
    const saved = JSON.parse(raw) as ConsentState
    if (Date.now() - saved.timestamp > CONSENT_MAX_AGE) {
      localStorage.removeItem(CONSENT_KEY)
      return null
    }
    return saved
  } catch {
    return null
  }
}

function saveConsent(analytics: boolean): ConsentState {
  const state: ConsentState = {
    analytics,
    timestamp: Date.now(),
    version: CONSENT_VERSION,
  }
  localStorage.setItem(CONSENT_KEY, JSON.stringify(state))
  return state
}

// ── Module-level reopener (allows footer to reopen consent) ───────────────────

let _reopenFn: (() => void) | null = null
export function reopenCookieConsent() {
  _reopenFn?.()
}

// ── Component ─────────────────────────────────────────────────────────────────

export function CookieConsent() {
  const [visible, setVisible] = useState(false)
  const [showManage, setShowManage] = useState(false)
  const [analyticsToggle, setAnalyticsToggle] = useState(false)
  const [jurisdiction] = useState<Jurisdiction>(() => detectJurisdiction())

  // Register the reopen function
  useEffect(() => {
    _reopenFn = () => {
      setShowManage(false)
      setVisible(true)
    }
    return () => { _reopenFn = null }
  }, [])

  // On mount: check for existing consent
  useEffect(() => {
    const saved = getSavedConsent()
    if (saved) {
      if (saved.analytics) loadUmami()
      return
    }
    // No saved consent — show banner
    setVisible(true)
  }, [])

  function handleAcceptAll() {
    saveConsent(true)
    loadUmami()
    setVisible(false)
    setShowManage(false)
  }

  function handleRejectNonEssential() {
    saveConsent(false)
    setVisible(false)
    setShowManage(false)
  }

  function handleSavePreferences() {
    saveConsent(analyticsToggle)
    if (analyticsToggle) loadUmami()
    setVisible(false)
    setShowManage(false)
  }

  if (!visible) return null

  // ── GDPR banner (opt-IN required) ─────────────────────────────────────────
  if (jurisdiction === 'gdpr') {
    if (showManage) {
      return (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookie-manage-title"
          className="logging-screen fixed inset-0 z-[300] flex flex-col justify-end"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setShowManage(false)}
            aria-hidden="true"
          />

          {/* Sheet */}
          <div className="relative bg-[var(--log-card)] rounded-t-[var(--log-radius)] shadow-[var(--log-shadow-lg)] max-w-[430px] w-full mx-auto">
            <div className="px-5 pt-5 pb-2 border-b border-[var(--log-ink-10)]">
              <h2
                id="cookie-manage-title"
                className="log-font-heading text-[17px] font-bold text-[var(--log-ink)]"
              >
                Cookie preferences
              </h2>
              <p className="mt-1 text-[13px] text-[var(--log-ink-60)]">
                You're in the UK or EU. We need your permission before loading any non-essential tracking.
              </p>
            </div>

            <div className="px-5 py-4 space-y-4">
              {/* Essential — always on */}
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <p className="text-[14px] font-semibold text-[var(--log-ink)]">Essential storage</p>
                  <p className="text-[12px] text-[var(--log-ink-60)] mt-0.5">
                    Authentication session, child profiles, meal logs, and app state. Required for the app to function.
                  </p>
                </div>
                <div className="flex-shrink-0 mt-0.5">
                  <span className="text-[12px] font-semibold text-[var(--log-green-mid)]">Always on</span>
                </div>
              </div>

              <hr className="border-[var(--log-ink-10)]" />

              {/* Analytics — toggleable */}
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <p className="text-[14px] font-semibold text-[var(--log-ink)]">Analytics</p>
                  <p className="text-[12px] text-[var(--log-ink-60)] mt-0.5">
                    Umami Analytics — cookieless, privacy-focused. Helps us understand how the app is used.
                    No personal data collected, no cross-site tracking.
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={analyticsToggle}
                  onClick={() => setAnalyticsToggle((v) => !v)}
                  className={`flex-shrink-0 mt-0.5 relative inline-flex h-[26px] w-[46px] rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--log-green-mid)] focus:ring-offset-1 ${
                    analyticsToggle ? 'bg-[var(--log-green-mid)]' : 'bg-[var(--log-ink-30)]'
                  }`}
                  aria-label="Toggle analytics"
                >
                  <span
                    className={`inline-block h-[22px] w-[22px] mt-[2px] rounded-full bg-white shadow transition-transform ${
                      analyticsToggle ? 'translate-x-[22px]' : 'translate-x-[2px]'
                    }`}
                  />
                </button>
              </div>
            </div>

            <div className="px-5 pb-6 pt-2 flex flex-col gap-2">
              <button
                type="button"
                onClick={handleSavePreferences}
                className="log-nav-btn primary w-full"
              >
                Save preferences
              </button>
              <button
                type="button"
                onClick={() => setShowManage(false)}
                className="w-full py-2.5 text-[13px] font-medium text-[var(--log-ink-60)] hover:text-[var(--log-ink)] transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )
    }

    // GDPR main banner
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-banner-title"
        className="logging-screen fixed bottom-0 left-0 right-0 z-[300] flex justify-center pointer-events-none"
      >
        <div className="pointer-events-auto max-w-[430px] w-full bg-[var(--log-card)] border-t-2 border-[var(--log-green-mid)] shadow-[var(--log-shadow-lg)] px-5 pt-4 pb-safe-6">
          <p id="cookie-banner-title" className="log-font-heading text-[14px] font-bold text-[var(--log-ink)]">
            We use analytics
          </p>
          <p className="mt-1.5 text-[12px] text-[var(--log-ink-60)] leading-relaxed">
            We'd like to use Umami Analytics (cookieless, privacy-focused) to understand how EnergyMatch is used.
            All other storage is essential and already active.{' '}
            <a
              href="/privacy-policy.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--log-green-mid)] underline underline-offset-2"
            >
              Privacy Policy
            </a>
          </p>
          <div className="mt-3 flex flex-col gap-2">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleAcceptAll}
                className="flex-1 min-h-[44px] rounded-[var(--log-radius-sm)] bg-[var(--log-green-mid)] text-white text-[13px] font-semibold hover:bg-[var(--log-green)] transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--log-green-mid)] focus:ring-offset-1"
              >
                Accept
              </button>
              <button
                type="button"
                onClick={handleRejectNonEssential}
                className="flex-1 min-h-[44px] rounded-[var(--log-radius-sm)] bg-[var(--log-ink-10)] text-[var(--log-ink)] text-[13px] font-semibold hover:bg-[var(--log-ink-10)] transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--log-ink-30)] focus:ring-offset-1"
              >
                Reject
              </button>
            </div>
            <button
              type="button"
              onClick={() => { setAnalyticsToggle(false); setShowManage(true) }}
              className="w-full py-2 text-[12px] font-medium text-[var(--log-ink-60)] hover:text-[var(--log-ink)] transition-colors"
            >
              Manage preferences
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── AU / PIPEDA banner (informational + opt-out) ───────────────────────────
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Cookie notice"
      className="logging-screen fixed bottom-0 left-0 right-0 z-[300] flex justify-center pointer-events-none"
    >
      <div className="pointer-events-auto max-w-[430px] w-full bg-[var(--log-card)] border-t border-[var(--log-ink-10)] shadow-[var(--log-shadow-lg)] px-5 pt-4 pb-safe-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <p className="log-font-heading text-[13px] font-bold text-[var(--log-ink)]">
              Analytics notice
            </p>
            <p className="mt-1 text-[12px] text-[var(--log-ink-60)] leading-relaxed">
              EnergyMatch uses Umami Analytics — cookieless and privacy-focused — to improve the app.
              No personal data leaves your device.{' '}
              <a
                href="/privacy-policy.html"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--log-green-mid)] underline underline-offset-2"
              >
                Learn more
              </a>
            </p>
          </div>
        </div>
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={handleAcceptAll}
            className="flex-1 min-h-[44px] rounded-[var(--log-radius-sm)] bg-[var(--log-green-mid)] text-white text-[13px] font-semibold hover:bg-[var(--log-green)] transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--log-green-mid)] focus:ring-offset-1"
          >
            OK
          </button>
          <button
            type="button"
            onClick={handleRejectNonEssential}
            className="flex-1 min-h-[44px] rounded-[var(--log-radius-sm)] bg-[var(--log-ink-10)] text-[var(--log-ink)] text-[13px] font-semibold hover:bg-[#e0ebe5] transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--log-ink-30)] focus:ring-offset-1"
          >
            Opt out
          </button>
        </div>
      </div>
    </div>
  )
}
